import { Localized } from "@fluent/react"
import { Alert, Avatar, Badge, Box, Button, ButtonGroup, Chip, IconButton, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from "@mui/material"
import { ReactNode, useCallback, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { AppBlockGeneric, AppLiGeneric, AppRowGeneric, Split } from "../ursOffer/importedUrsSlice"
import { createUrsOfferComment, postUrsOfferRequirementNote, RequirementNote, UrsOffer, UrsOfferMessage, UrsOfferRequirement, UrsOfferStatus } from "./ursOfferSlice"
import { BlockMeta } from "../urs/ImportedSupplierUrsPreview"
import InsertCommentIcon from '@mui/icons-material/InsertComment'
import { Check, Clear } from "@mui/icons-material"
import QuestionMarkIcon from '@mui/icons-material/QuestionMark'
import { LoadingButton } from "@mui/lab"
import { AppId, AppTextFieldHandler } from "../../app/appTypes"
import CheckIcon from '@mui/icons-material/Check'
import EditIcon from '@mui/icons-material/Edit'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import { impossible, parseAndFormatISODate } from "../../app/Utils"
import { selectUserById } from "../users/usersSlice"
import { selectLoggedUserId } from "../user/userSlice"
import { selectUrsOfferRequirementById, updateUrsOfferRequirement } from "./ursOfferRequirementsSlice"

type AssignToHandler = (arg: UrsOfferRequirement) => void
type CommentSetter = (f: (comments: UrsOfferMessage[]) => void) => void

export interface ImportedOfferRequirementsPanelProps {
    urs: UrsOffer
    template: boolean
    comments: UrsOfferMessage[]
    assignTo: AssignToHandler
    updateComments: CommentSetter
    onlyNotEvaluated: boolean
    assignedToMe: boolean
    showComments: (ursOfferRequirement: UrsOfferRequirement) => void
}
export const NoRequirements = () => {
    return <Typography gutterBottom component="div" color="textSecondary" variant="h5">
        <Localized id="urs-no-requirements">Brak wymagań.</Localized>
    </Typography>
}

export const ImportedOfferRequirementsFlatPanel = (props: ImportedOfferRequirementsPanelProps) => {
    const { urs, template, comments, assignTo, updateComments, onlyNotEvaluated, showComments, assignedToMe } = props
    const { id, status, actions  } = urs

    const ursRequirements = urs.types[0].requirements
    let blocks: AppBlockGeneric<UrsOfferRequirement>[] = []
    let metas: BlockMeta[] = []
    for (let i = 0; i < ursRequirements.length; i++) {
        const r = ursRequirements[i]
        const { jsonContent } = r
        if (jsonContent) {
            const meta: BlockMeta = JSON.parse(jsonContent) as BlockMeta
            switch (meta.type) {
                case 'h':
                    blocks.push({
                        type: 'h',
                        content: meta.content,
                        data: r,
                    })
                    break
                case 'p':
                    blocks.push({
                        type: 'p',
                        content: meta.content,
                        data: r,
                    })
                    break
                case 'ol':
                    blocks.push({
                        type: 'ol',
                        items: r.childRequirements.map(childReq => {
                            return {
                                content: childReq.name,
                                data: childReq,
                            }
                        }),
                        data: r,
                    })
                    break
                case 'ul':
                    blocks.push({
                        type: 'ul',
                        items: r.childRequirements.map(childReq => {
                            return {
                                content: childReq.name,
                                data: childReq,
                            }
                        }),
                        data: r,
                    })
                    break
                case 'th':
                case 'tr':
                    if (i > 0) {
                        const prev = metas[i-1]
                        if ((prev.type === 'th' || prev.type === 'tr') && prev.tableId === meta.tableId) {
                            const tab = blocks[blocks.length-1]
                            if (tab.type === 'table') {
                                tab.rows.push({
                                    type: meta.type,
                                    cols: meta.cols,
                                    plain: r.name,
                                    data: r,
                                    split: meta.split,
                                })
                            } else {
                                console.log("[BUG] expected 'table'")
                            }
                        } else {
                            blocks.push({
                                type: 'table',
                                colsCount: meta.cols.length,
                                rows: [{
                                    type: meta.type,
                                    cols: meta.cols,
                                    plain: r.name,
                                    data: r,
                                    split: meta.split,
                                }]
                            })
                        }
                    } else {
                        blocks.push({
                            type: 'table',
                            colsCount: meta.cols.length,
                            rows: [{
                                type: meta.type,
                                cols: meta.cols,
                                plain: r.name,
                                data: r,
                                split: meta.split,
                            }]
                        })
                    }
                    break
            }
            metas.push(meta)
        }
    }

    return <Box sx={{
        height: 'calc(100vh - 400px)',
        p: 0.5,
        overflow: 'auto',
        width: '100%',
        minWidth: '100%',
    }} >
        {blocks.map((b, i) => <RenderBlock
            block={b}
            offerId={id}
            comments={comments}
            assignTo={assignTo}
            status={status}
            actions={actions.contentActions ?? []}
            updateComments={updateComments}
            onlyNotEvaluated={onlyNotEvaluated}
            showComments={showComments}
            assignedToMe={assignedToMe}
        />)}
    </Box>
}

interface BlockProps {
    block : AppBlockGeneric<UrsOfferRequirement>
    offerId: AppId
    comments: UrsOfferMessage[]
    assignTo: AssignToHandler
    status: UrsOfferStatus
    onlyNotEvaluated: boolean
    actions: string[]
    updateComments: CommentSetter
    showComments: (ursOfferRequirement: UrsOfferRequirement) => void
    assignedToMe: boolean
}
const RenderBlock = (props: BlockProps) => {
    const { block,  ...rest } = props
    const { offerId, comments, assignTo, status, actions, updateComments, onlyNotEvaluated, showComments, assignedToMe } = rest

    switch (block.type) {
        case 'h':
            return <Box sx={{ display: 'flex' }}>
                <Typography  variant="h6" sx={{ flexGrow: 1 }} >
                    <div dangerouslySetInnerHTML={{ __html: block.content }}></div>
                </Typography>
                <Box sx={{
                    ml: .5,
                    width: '400px',
                    borderLeft: '1px dashed var(--gray-300)',
                    flex: 'none',
                }}></Box>
            </Box>
        case 'p': return <ParagraphBlock {...rest} requiremntId={block.data.id} />
        case 'ol':
            return <ol>
                {block.items.map(i => <AppUnorderedListItem {...props} item={i} />)}
            </ol>
        case 'ul':
            return <ul>
                {block.items.map(i => <AppUnorderedListItem {...props} item={i} />)}
            </ul>
        case 'table':
            return <AppTable
                offerId={offerId}
                colsCount={block.colsCount}
                rows={block.rows}
                comments={comments}
                assignTo={assignTo}
                status={status}
                actions={actions}
                updateComments={updateComments}
                onlyNotEvaluated={onlyNotEvaluated}
                showComments={showComments}
                assignedToMe={assignedToMe}
            />
    }
}

interface ListItemProps {
    item: AppLiGeneric<UrsOfferRequirement>
    offerId: AppId
    comments: UrsOfferMessage[]
    assignTo: AssignToHandler
    status: UrsOfferStatus
    actions: string[]
    updateComments: CommentSetter
    showComments: (ursOfferRequirement: UrsOfferRequirement) => void
}
const AppUnorderedListItem = (props: ListItemProps) => {
    const { item, offerId, comments, assignTo, status, actions, updateComments, showComments } = props
    const rightComments = comments.filter(x => x.ursRequirementId == item.data.id && x.parentMessageId === null)
    const firstComment = rightComments.at(0)
    const commentsLength = rightComments.length

    return <li>
        <Box sx={{
            display: 'flex',
        }}>
            <Typography sx={{
                flexGrow: 1,
            }} component='div' variant="body1" dangerouslySetInnerHTML={{ __html: item.content }}>
            </Typography>
            <Box sx={{
                ml: .5,
                width: '400px',
                borderLeft: '1px dashed var(--gray-300)',
                flex: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pl: 1,
            }}>
                <Stack gap={.25} sx={{ width: '100%', alignItems: 'center' }}>
                    <CommentSection
                        offerId={offerId}
                        r={item.data}
                        assignTo={assignTo}
                        status={status}
                        actions={actions}
                        comments={comments}
                        updateComments={updateComments}
                        showComments={showComments}
                    />
                    {firstComment ? <CommentBox {...firstComment} showComments={() => showComments(item.data)} commentsLength={commentsLength} /> : null }
                </Stack>
            </Box>
        </Box>
    </li>
}

interface ParagraphBlockProps {
    requiremntId: AppId
    offerId: AppId
    comments: UrsOfferMessage[]
    assignTo: AssignToHandler
    status: UrsOfferStatus
    actions: string[]
    updateComments: CommentSetter
    onlyNotEvaluated: boolean
    showComments: (ursOfferRequirement: UrsOfferRequirement) => void
    assignedToMe: boolean
}
const ParagraphBlock = (props: ParagraphBlockProps) => {
    const { requiremntId, offerId, comments, assignTo, status, actions, updateComments, onlyNotEvaluated, showComments, assignedToMe } = props
    const offerRequirement = useAppSelector(state => selectUrsOfferRequirementById(state.ursOfferRequirements, requiremntId)) ?? impossible()
    const currentUserId = useAppSelector(selectLoggedUserId)

    if (onlyNotEvaluated && (offerRequirement.note === 'D' || offerRequirement.note === 'U' || offerRequirement.note === 'F')) {
        return null
    }
    if (assignedToMe && offerRequirement.responsibleUserId !== currentUserId) {
        return null
    }

    const rightComments = comments.filter(x => x.ursRequirementId == requiremntId && x.parentMessageId === null)
    const firstComment = rightComments.at(0)
    const commentsLength = rightComments.length

    return <Box sx={{
            display: 'flex',
        }}>
        <Typography sx={{
            flexGrow: 1,
        }} component='div' variant="body1" dangerouslySetInnerHTML={{ __html: offerRequirement.name }}>
        </Typography>
        <Box sx={{
            ml: .5,
            width: '400px',
            borderLeft: '1px dashed var(--gray-300)',
            flex: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pl: 1,
        }}>
            <Stack gap={.25} sx={{ width: '100%', alignItems: 'center' }}>
                <CommentSection
                    offerId={offerId}
                    r={offerRequirement}
                    assignTo={assignTo}
                    status={status}
                    actions={actions}
                    comments={comments}
                    updateComments={updateComments}
                    showComments={showComments}
                />
                {firstComment ? <CommentBox {...firstComment} showComments={() => showComments(offerRequirement)} commentsLength={commentsLength} /> : null }
            </Stack>
        </Box>
    </Box>
}


interface TableProps {
    offerId: AppId
    colsCount: number
    rows: AppRowGeneric<UrsOfferRequirement>[]
    comments: UrsOfferMessage[]
    assignTo: AssignToHandler
    status: UrsOfferStatus
    actions: string[]
    updateComments: CommentSetter
    onlyNotEvaluated: boolean
    showComments: (ursOfferRequirement: UrsOfferRequirement) => void
    assignedToMe: boolean
}

function AppTable(props: TableProps) {
    const { colsCount, rows, offerId, comments, assignTo, status, actions, updateComments, onlyNotEvaluated, showComments, assignedToMe } = props
    const firstTrIdx = rows.findIndex(r => r.type === 'tr')
    const ths = rows.slice(0, firstTrIdx)
    const trs = rows.slice(firstTrIdx)
    return <TableContainer sx={{
        my: 1,
    }} component={Paper}>
        <Table>
            {ths.length > 0 && <TableHead>
                {ths.map(row => <TableRow>
                    {row.cols.map(col => <TableCell sx={{ minWidth: '150px' }}><span dangerouslySetInnerHTML={{ __html: col}}></span></TableCell>)}
                    <TableCell className="comment-cell" sx={{ width: '400px', borderLeft: '1px dashed var(--gray-300)', }}></TableCell>
                </TableRow>)}
            </TableHead>}
            <TableBody>
                {trs.map(row => row.split
                    ? <AppSplitTableRows
                        row={row}
                        offerId={offerId}
                        comments={comments}
                        assignTo={assignTo}
                        status={status}
                        actions={actions}
                        updateComments={updateComments}
                        onlyNotEvaluated={onlyNotEvaluated}
                        split={row.split}
                        showComments={showComments}
                        assignedToMe={assignedToMe}
                    />
                    : <AppTableRow
                        row={row}
                        offerId={offerId}
                        comments={comments}
                        assignTo={assignTo}
                        status={status}
                        actions={actions}
                        updateComments={updateComments}
                        onlyNotEvaluated={onlyNotEvaluated}
                        showComments={showComments}
                        assignedToMe={assignedToMe}
                />)}
            </TableBody>
        </Table>
    </TableContainer>;
}

interface TableRowProps {
    row: AppRowGeneric<UrsOfferRequirement>
    offerId: AppId
    comments: UrsOfferMessage[]
    assignTo: AssignToHandler
    status: UrsOfferStatus
    actions: string[]
    updateComments: CommentSetter
    onlyNotEvaluated: boolean
    showComments: (ursOfferRequirement: UrsOfferRequirement) => void
    assignedToMe: boolean
}
const AppTableRow = (props: TableRowProps) => {
    const { row, offerId, comments, assignTo, status, actions, updateComments, onlyNotEvaluated, showComments, assignedToMe } = props
    const offerRequirement = useAppSelector(state => selectUrsOfferRequirementById(state.ursOfferRequirements, row.data.id)) ?? impossible()
    const currentUserId = useAppSelector(selectLoggedUserId)

    if (onlyNotEvaluated && (offerRequirement.note === 'D' || offerRequirement.note === 'U' || offerRequirement.note === 'F')) {
        return null
    }
    if (assignedToMe && offerRequirement.responsibleUserId !== currentUserId) {
        return null
    }
    const rightComments = comments.filter(x => x.ursRequirementId == row.data.id && x.parentMessageId === null)
    const firstComment = rightComments.at(0)
    const commentsLength = rightComments.length

    return <TableRow>
        {row.cols.map(col => <TableCell>
            <p dangerouslySetInnerHTML={{ __html: col }}></p>
        </TableCell>)}
        <TableCell sx={{
            borderLeft: '1px dashed var(--gray-300)',
            width: '400px',
            textAlign: 'center',
            verticalAlign: 'top',
        }}>
            <Stack gap={.25} >
                <CommentSection
                    offerId={offerId}
                    r={row.data}
                    assignTo={assignTo}
                    status={status}
                    actions={actions}
                    comments={comments}
                    updateComments={updateComments}
                    showComments={showComments}
                />
                {firstComment ? <CommentBox {...firstComment} showComments={() => showComments(offerRequirement)} commentsLength={commentsLength} /> : null }
            </Stack>
        </TableCell>
    </TableRow>
}

type SplitTableRowProps = TableRowProps & {
    split: Split
}
const AppSplitTableRows = (props: SplitTableRowProps) => {
    const { row, offerId, comments, assignTo, status, actions, updateComments, onlyNotEvaluated, split, showComments, assignedToMe } = props
    const offerRequirement = useAppSelector(state => selectUrsOfferRequirementById(state.ursOfferRequirements, row.data.id)) ?? impossible()
    const currentUserId = useAppSelector(selectLoggedUserId)

    if (split.content.length === 0) return null

    if (assignedToMe && !offerRequirement.childRequirements.some(c => c.responsibleUserId === currentUserId)) {
        return null
    }

    let rows: ReactNode[] = []
    const children = row.data.childRequirements
    for (let i = 1; i < children.length; i++) {
        rows.push( <AppSplitTableRow {...props} child={children[i]} />)
    }

    return <>
        <AppFstSplitTableRow {...props} />
        {rows}
    </>
}

const AppFstSplitTableRow = (props: SplitTableRowProps) => {
    const { row, offerId, comments, assignTo, status, actions, updateComments, onlyNotEvaluated, split, showComments } = props
    const rightComments = comments.filter(x => x.ursRequirementId == row.data.id && x.parentMessageId === null)
    const firstComment = rightComments.at(0)
    const commentsLength = rightComments.length
    const children = row.data.childRequirements
    
    return <TableRow>{row.cols.map((value, colIdx) => {
            if (colIdx !== split.colIndex) {
                return <TableCell rowSpan={split.content.length}>
                    <p dangerouslySetInnerHTML={{ __html: value }}></p>
                </TableCell>
            } else {
                return <TableCell>
                    <p dangerouslySetInnerHTML={{ __html: children[0].name }}></p>
                </TableCell>
            }
        })}
        <TableCell sx={{
            borderLeft: '1px dashed var(--gray-300)',
            width: '400px',
            textAlign: 'center',
            verticalAlign: 'top',
        }}>
            <Stack gap={.25} >
                <CommentSection
                    offerId={offerId}
                    r={row.data}
                    assignTo={assignTo}
                    status={status}
                    actions={actions}
                    comments={comments}
                    updateComments={updateComments}
                    showComments={showComments}
                />
                {firstComment ? <CommentBox {...firstComment} showComments={() => showComments(row.data)} commentsLength={commentsLength} /> : null }
            </Stack>
        </TableCell>
    </TableRow>
}

const AppSplitTableRow = (props: SplitTableRowProps & { child: UrsOfferRequirement }) => {
    const { row, offerId, comments, assignTo, status, actions, updateComments, onlyNotEvaluated, split, showComments, child } = props
    const rightComments = comments.filter(x => x.ursRequirementId == child.id && x.parentMessageId === null)
    const firstComment = rightComments.at(0)
    const commentsLength = rightComments.length
    
    return <TableRow>
        <TableCell>
            <p dangerouslySetInnerHTML={{ __html: child.name }}></p>
        </TableCell>
        <TableCell sx={{
            borderLeft: '1px dashed var(--gray-300)',
            width: '400px',
            textAlign: 'center',
            verticalAlign: 'top',
        }}>
            <Stack gap={.25} >
                <CommentSection
                    offerId={offerId}
                    r={child}
                    assignTo={assignTo}
                    status={status}
                    actions={actions}
                    comments={comments}
                    updateComments={updateComments}
                    showComments={showComments}
                />
                {firstComment ? <CommentBox {...firstComment} showComments={() => showComments(child)} commentsLength={commentsLength} /> : null }
            </Stack>
        </TableCell>
    </TableRow>
}

interface SectionProps {
    r: UrsOfferRequirement
    offerId: AppId
    assignTo: AssignToHandler
    status: UrsOfferStatus
    actions: string[]
    comments: UrsOfferMessage[]
    updateComments: CommentSetter
    showComments: (ursOfferRequirement: UrsOfferRequirement) => void
}

const CommentSection = (props: SectionProps) => {
    const dispatch = useAppDispatch()
    const { r, offerId, assignTo, status, actions, comments, showComments } = props
    const { comment } = r
    const offerRequirement = useAppSelector(state => selectUrsOfferRequirementById(state.ursOfferRequirements, r.id)) ?? impossible()
    const currentUserId = useAppSelector(selectLoggedUserId)

    const [edit, setEdit] = useState(false)
    const [reqNote, setReqNote] = useState<RequirementNote | null>(r.note)
    const [reqComment, setReqComment] = useState(r.comment)
    const [saving, setSaving] = useState(false)

    const editable = actions.findIndex(a => a === 'EDIT') >= 0

    const handleSaveClick = async () => {
        if (reqNote) {
            setSaving(true)
            try {
                await dispatch(postUrsOfferRequirementNote({
                    id: offerId,
                    requirementId: r.id,
                    comment: reqComment,
                    note: reqNote,
                })).unwrap()
                dispatch(updateUrsOfferRequirement({
                    ...offerRequirement,
                    note: reqNote,
                    comment: reqComment,
                }))
                setEdit(false)
            } catch (error) {
                console.log(error)
            } finally {
                setSaving(false)
            }
        }
    }

    const handleCheckClick = async () => {
        setSaving(true)
        try {
            const updatedOfferRequirement = await dispatch(postUrsOfferRequirementNote({
                id: offerId,
                requirementId: r.id,
                comment: '',
                note: 'F',
            })).unwrap()
            dispatch(updateUrsOfferRequirement({
                ...offerRequirement,
                note: 'F',
                comment: '',
            }))
            setReqNote('F')
        } catch (error) {
            console.log(error)
        } finally {
            setSaving(false)
        }
    }

    const handleClarificationNeededClick = async () => {
        setSaving(true)
        try {
            const updatedOfferRequirement = await dispatch(postUrsOfferRequirementNote({
                id: offerId,
                requirementId: r.id,
                comment: 'Clarification needed',
                note: 'D',
            })).unwrap()
            dispatch(updateUrsOfferRequirement({
                ...offerRequirement,
                note: 'D',
                comment: 'Clarification needed',
            }))
            setReqNote('D')
            setReqComment('Clarification needed')
        } catch (error) {
            console.log(error)
        } finally {
            setSaving(false)
        }
    }

    const handleAssignToClick = useCallback(() => {
        assignTo(offerRequirement)
    }, [])

    const handleReqCommentChange: AppTextFieldHandler = useCallback(e => setReqComment(e.target.value), [])
    const handleReqCommentStatusChange: AppTextFieldHandler = useCallback(e => setReqNote(e.target.value as RequirementNote), [])

    const opinioned = comments.findIndex(x => x.userId === r.responsibleUserId && x.ursRequirementId == r.id) >= 0

    const handleEditClick = () => {
        setEdit(true)
        setReqNote(offerRequirement.note)
        setReqComment(offerRequirement.comment)
    }

    // status == O => Opinion
    if (status === 'N' && offerRequirement.responsibleUserId && currentUserId === offerRequirement.responsibleUserId) {
        return <Opinion {...props} />
    } else if (edit) {
        return <Box sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            p: 1,
            border: '1px solid var(--slate-300)',
            borderRadius: '.5rem',
            backgroundColor: 'var(--slate-50)',
        }}>
            <TextField
                sx={{ backgroundColor: '#fff' }}
                value={reqNote}
                fullWidth
                select
                label={<Localized id="urs-offer-req-opinion"><span>Ocena</span></Localized>}
                size='small'
                onChange={handleReqCommentStatusChange}
            >
                <MenuItem value={"F"}><Check fontSize="small" color='success' /> <Localized id="urs-offer-req-f"><span> Spełnia</span></Localized></MenuItem>
                <MenuItem value={"U"}><Clear fontSize="small" color='error' /><Localized id="urs-offer-req-u"><span> Nie spełnia</span></Localized></MenuItem>
                <MenuItem value={"D"}><QuestionMarkIcon fontSize="small" color='warning' /><Localized id="urs-offer-req-d"><span> Do oceny</span></Localized></MenuItem>

            </TextField>
            <TextField
                sx={{ backgroundColor: '#fff' }}
                fullWidth
                multiline
                minRows={2}
                size='small'
                inputProps={{ maxLength: 1000 }}
                value={reqComment}
                onChange={handleReqCommentChange}
                label={<Localized id="urs-offer-req-comment"><span>Komentarz</span></Localized>}
            />
            <ButtonGroup>
                <LoadingButton loading={saving} size='small' onClick={handleSaveClick}>
                    <Localized id='save'>Save</Localized>
                </LoadingButton>
                <LoadingButton loading={saving} size='small' color='error' onClick={() => setEdit(false)}>
                    <Localized id='cancel'>Cancel</Localized>
                </LoadingButton>
            </ButtonGroup>
        </Box>
    } else {
        if (offerRequirement.note === null) {
            if (editable) {
                return <>
                <Box sx={{
                        display: 'felx',
                        flexDirection: 'row',
                        gap: 1,
                    }}>
                        <Tooltip title='Edit'>
                            <IconButton disabled={saving} size='small' onClick={handleEditClick}><InsertCommentIcon /></IconButton>
                        </Tooltip>
                        <Tooltip title='OK'>
                            <IconButton disabled={saving} size='small' onClick={handleCheckClick}><CheckIcon /></IconButton>
                        </Tooltip>
                        <Tooltip title='Clarificatio needed'>
                            <IconButton disabled={saving} size='small' onClick={handleClarificationNeededClick}><QuestionMarkIcon /></IconButton>
                        </Tooltip>
                        <Tooltip title='Assign person'>
                            <IconButton disabled={saving} size='small' onClick={handleAssignToClick}><PersonAddIcon /></IconButton>
                        </Tooltip>
                    </Box>
                    {offerRequirement.responsibleUserId && !opinioned && <FullUserInfo userId={offerRequirement.responsibleUserId ?? ''} />}
                </>
            } else {
                return <OptionalOpinion {...props} />
            }
        }  else {
            let label = <></>
            switch (offerRequirement.note) {
                case 'F':
                    label = <Alert sx={{ width: '100%' }}
                        action={<IconButton onClick={handleEditClick} size='small'><EditIcon sx={{ width: '0.75em', height: '0.75em' }} /></IconButton>}
                        icon={<CheckIcon fontSize="inherit" />}
                        severity="success">{offerRequirement.comment ? offerRequirement.comment : 'OK'}</Alert>
                    break
                case 'U':
                    label = <Alert sx={{ width: '100%' }}
                        action={<IconButton onClick={() => setEdit(true)} size='small'><EditIcon sx={{ width: '0.75em', height: '0.75em' }} /></IconButton>}
                        icon={<Clear fontSize="inherit" />} severity="error">{offerRequirement.comment ? offerRequirement.comment : 'NOT OK'}</Alert>
                    break
                case 'D':
                    label = <Alert sx={{ width: '100%' }}
                        action={<IconButton onClick={() => setEdit(true)} size='small'><EditIcon sx={{ width: '0.75em', height: '0.75em' }} /></IconButton>}
                        icon={<QuestionMarkIcon fontSize="inherit" />} variant='outlined' severity="success">{offerRequirement.comment ? offerRequirement.comment : 'OK'}</Alert>
                    break;
            }
            return label
        }
    }
}

const OptionalOpinion = (props: SectionProps) => {
    const { r, offerId, assignTo, status, actions, comments } = props
    const currentUserId = useAppSelector(selectLoggedUserId)
    const myOpinion = comments.find(x => x.userId === currentUserId && x.ursRequirementId == r.id)
    const [addCommentMode, setAddCommentMode] = useState(false)
    const [opinion, setOpinion] = useState(myOpinion?.message ?? '')

    const handleCancelClick = () => {
        setAddCommentMode(false)
    }

    if (myOpinion) {
        return null
    } else if (addCommentMode) {
        return <Opinion {...props} cancel={handleCancelClick} />
    } else {
        return <Button size='small' variant='text' startIcon={<InsertCommentIcon />} onClick={() => setAddCommentMode(true)}>Add comment</Button>
    }
}

const CommentBox = ({ message, date, companyName, author, showComments, commentsLength }: UrsOfferMessage & { showComments: () => void, commentsLength: number }) => {
    // const created = parseAndFormatISODate(date, true)
    return <Box sx={{
        backgroundColor: 'var(--yellow-300)',
        border: '1px solid var(--gray-400)',
        borderRadius: 1,
        p: 1,
        width: '100%',
    }}>
        <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1,
        }}>
            <Typography component='span' variant="body2" fontWeight='bold'>{author} </Typography>
            {/* <Typography component='span' variant="body2" color='text.secondary' fontSize='small'>{created}</Typography> */}
            <Typography component='span' variant="body2" color='text.secondary' fontSize='small'>{companyName}</Typography>
        </Box>
        <Typography variant="body2">{message ?? ''}</Typography>
        <Badge variant='standard' badgeContent={commentsLength - 1} color='primary'>
            <Button size='small' onClick={showComments}>Open discussion</Button>
        </Badge>
    </Box>
}


export const FullUserInfo = ({ userId }: { userId: string }) => {
    const user = useAppSelector(state => selectUserById(state, userId))
    if (!userId) 
        return null
    else if (!user) 
        return <>User not found</>
    else return <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: .5,
        }}>
        <Chip sx={{ mr: 1 }} label={'Assigned to'} color='info' />
        <Avatar sx={{ width: 40, height: 40 }} />
        <Stack>
            <Typography sx={{ mb: 0, display: 'flex' }} component='div' gutterBottom variant="body1" fontWeight={500}>
                <span>{user.firstname}&nbsp;{user.surname}</span>
            </Typography>
            <Typography sx={{ mb: 0, display: 'flex' }} component='div' gutterBottom variant="body2" color='text.secondary'>
                {user.position}
            </Typography>
        </Stack>
    </Box>
}

export const Opinion = (props: SectionProps & { cancel?: () => void }) => {
    const { offerId, r, comments, updateComments, cancel } = props
    const dispatch = useAppDispatch()

    const currentUserId = useAppSelector(selectLoggedUserId)

    const myComment = comments.find(x => x.userId === currentUserId && x.discussionType === 'PRIVATE_COMPANY' && x.ursRequirementId == r.id)
    const [draftNote, setDraftNote] = useState(myComment?.message ?? '')
    const [saving, setSaving] = useState(false)

    const handlePostCommentClick = async () => {
        if (draftNote) {
            setSaving(true)
            try {
                const newComment = await dispatch(createUrsOfferComment({
                    id: "",
                    ursOfferId: offerId,
                    ursRequirementId: r.id,
                    message: draftNote,
                    author: "",
                    companyName: "",
                    userId: currentUserId,
                    date: new Date().toISOString(),
                    parentMessageId: null,
                    status: "",
                    guid: '',
                    discussionType: 'PRIVATE_COMPANY',
                    type: 'text',
                })).unwrap()
                updateComments(cs => cs.push(newComment))
            } catch (error) {
                console.log(error)
            } finally {
                setSaving(false)
            }
        }
    }

    const handleCancelClick = () => {
        cancel?.()
    }

    if (myComment) return null

    return <Box sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            p: 1,
            border: '1px solid var(--slate-300)',
            borderRadius: '.5rem',
            backgroundColor: 'var(--slate-50)',
        }}>
            <TextField
                sx={{ backgroundColor: '#fff' }}
                fullWidth
                multiline
                minRows={2}
                size='small'
                inputProps={{ maxLength: 1000 }}
                value={draftNote}
                onChange={e => setDraftNote(e.target.value)}
                label={<Localized id="urs-offer-req-comment"><span>Komentarz</span></Localized>}
            />
            <ButtonGroup>
                <LoadingButton loading={saving} size='small' onClick={handlePostCommentClick}>
                    <Localized id='save'>Save</Localized>
                </LoadingButton>
                {cancel && <LoadingButton loading={saving} size='small' color='error' onClick={handleCancelClick}>
                    <Localized id='cancel'>Cancel</Localized>
                </LoadingButton>}
            </ButtonGroup>
        </Box>
}
